/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PregnancyModalContainer,
  MessageContainer,
  EmptyMessage,
  LoadingContainer,
  ContentInLine,
  SkeletonItem,
  ContentInLineHeader
} from './styles'
import { useMaternalInfantHealthProcedures, usePatient } from '~/context'
import { IFetchPregnancy } from '~/interfaces'
import { PortalModal } from '~/components/shared/PortalModal'
import { DEFAULT_PATH, TIMELINE_GENERAL_PATH } from '~/constants'
import { message } from '~/services'
import { parseModalPregnancies } from '~/utils'
import { useMaternalInfantViewSettings } from '~/hooks'
import { Table } from '~/components/shared'

const NO_BABY = undefined

interface IProps {
  show: boolean
  onCloseModal: () => void
}

export const ModalSelectPregnancy = ({ show, onCloseModal }: IProps): JSX.Element => {
  const navigate = useNavigate()
  const { patient } = usePatient()
  const {
    selectedPregnancy,
    updateSelectedPregnancy,
    updateSelectedBaby,
    updateSelectedTimeline,
    pregnancies,
    thereArePregnancies,
    thereAreNoPregnancies,
    isPatientPregnanciesLoading,
    fetchPregnanciesError,
    thereIsFetchPregnanciesError
  } = useMaternalInfantHealthProcedures()
  const { updateMaternalInfantViewSettings } = useMaternalInfantViewSettings()

  const isActualOptionsDifferentOf = (option: IFetchPregnancy): boolean => {
    if (option.children && selectedPregnancy.children) {
      return option.children.map(c => c.id).join() !== selectedPregnancy?.children.map(c => c.id).join()
    }

    return true
  }

  const handleSelectPregnancy = (newSelectedPregnancy: IFetchPregnancy) => {
    if (isActualOptionsDifferentOf(newSelectedPregnancy)) {
      let defaultBaby: number | undefined = NO_BABY

      const newSelectedPregnancyHasBabies = newSelectedPregnancy.children.length > 0
      if (newSelectedPregnancyHasBabies) {
        defaultBaby = newSelectedPregnancy.children[0].id
      }

      updateSelectedPregnancy(newSelectedPregnancy)
      updateSelectedTimeline('mother')
      updateSelectedBaby(defaultBaby as number)

      updateMaternalInfantViewSettings({
        medicalRecord: patient.medicalRecord.toString(),
        selectedPregnancy: newSelectedPregnancy,
        selectedBaby: defaultBaby as number,
        selectedTimeline: 'mother'
      })
    }
    onCloseModal()
  }

  const Loader = (): JSX.Element => {
    return (
      <LoadingContainer data-testid="skeletonLoad">
        <ContentInLineHeader>
          <SkeletonItem width={'140px'} height={'20px'} />
          <SkeletonItem width={'140px'} height={'20px'} />
          <SkeletonItem width={'140px'} height={'20px'} />
          <SkeletonItem width={'140px'} height={'20px'} />
        </ContentInLineHeader>
        <ContentInLine>
          <SkeletonItem width={'560px'} height={'30px'} />
          <SkeletonItem width={'100px'} height={'30px'} />
        </ContentInLine>
      </LoadingContainer>
    )
  }

  const Message = (text?: string): JSX.Element => {
    return (
      <MessageContainer>
        <EmptyMessage data-testid="Message">{text || message('maternalInfant.empty_modal_section')}</EmptyMessage>
      </MessageContainer>
    )
  }

  const List = (contentList: IFetchPregnancy[]): JSX.Element => {
    return (
      <Table
        contentBackground="transparent"
        data={contentList}
        headerAlign="auto"
        headerLineHeight="47px"
        headerPadding="16px 0 16px 24px"
        lineHeight="50px"
        model={[
          {
            key: 'dum',
            label: 'DUM',
            width: '140px'
          },
          {
            key: 'dpp',
            label: 'DPP',
            width: '140px'
          },
          {
            key: 'dateOfDelivery',
            label: 'DATA DO PARTO',
            width: '140px'
          },
          {
            key: 'typeOfDelivery',
            label: 'TIPO DO PARTO',
            width: '140px'
          },
          {
            key: 'action',
            Component: value => {
              return (
                <button className="buttonSelectionModal" onClick={() => handleSelectPregnancy(value)}>
                  Selecionar
                </button>
              )
            },
            label: '',
            width: '100px'
          }
        ]}
        totalPages={1}
      />
    )
  }

  const renderContent = () => {
    if (thereIsFetchPregnanciesError()) return Message(fetchPregnanciesError as string)

    if (isPatientPregnanciesLoading) return Loader()

    if (thereArePregnancies()) return List(parseModalPregnancies(pregnancies))
    return Message()
  }

  const navigateToGeneralMedicalView = () => {
    navigate(`/${DEFAULT_PATH}/${TIMELINE_GENERAL_PATH}/${patient.medicalRecord}`)
  }

  const handleCloseModal = () => {
    onCloseModal()

    if (thereAreNoPregnancies()) navigateToGeneralMedicalView()
  }

  return (
    <PortalModal data-testid="ModalSelectPregnancy" onClose={handleCloseModal} show={show}>
      <PregnancyModalContainer>
        <h1 className="TitleModal">Selecione a Gestação</h1>
        <hr className="SeparatorModal" />
        {renderContent()}
      </PregnancyModalContainer>
    </PortalModal>
  )
}
